import React, { useMemo } from 'react';
import { ServerType } from '../types';

interface ServerEfficiencyDashboardProps {
  serverTypes: ServerType[];
  formatCurrency: (amount: number, decimals?: number) => string;
  unlockLevel: number; // Current unlock level to determine which servers to show
  reputation?: number; // Optional reputation to factor into revenue calculations
  powerEfficiencyUpgrade?: number; // Optional power efficiency upgrade level
}

const ServerEfficiencyDashboard: React.FC<ServerEfficiencyDashboardProps> = ({
  serverTypes,
  formatCurrency,
  unlockLevel,
  reputation = 100, // Default to 100% if not provided
  powerEfficiencyUpgrade = 0 // Default to 0 if not provided
}) => {
  // Calculate server efficiency metrics
  const serverMetrics = useMemo(() => {
    return serverTypes
      .filter(server => server.unlockLevel <= unlockLevel) // Only show unlocked servers
      .map(server => {
        // Apply power efficiency upgrade (reduces power consumption by 10% per level)
        const adjustedPowerDraw = server.powerDraw * (1 - (powerEfficiencyUpgrade * 0.1));
        
        // Calculate revenue per kW (considering reputation)
        const reputationMultiplier = reputation / 100;
        const revenuePerKw = (server.revenue * reputationMultiplier) / adjustedPowerDraw;
        
        // Calculate ROI (months to break even)
        const monthlyRevenue = server.revenue * reputationMultiplier;
        const roi = monthlyRevenue > 0 ? server.cost / monthlyRevenue : Infinity;
        
        // Calculate efficiency score (higher is better, normalized)
        const efficiencyScore = revenuePerKw / server.cost * 10000;
        
        return {
          ...server,
          adjustedPowerDraw,
          revenuePerKw,
          roi,
          efficiencyScore
        };
      })
      // Sort by efficiency score (highest first)
      .sort((a, b) => b.efficiencyScore - a.efficiencyScore);
  }, [serverTypes, unlockLevel, reputation, powerEfficiencyUpgrade]);

  // Define bar color based on efficiency score
  const getBarColor = (score: number): string => {
    if (score >= 20) return 'bg-green-500';
    if (score >= 10) return 'bg-lime-500';
    if (score >= 5) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4 mb-4">
      <h2 className="text-xl font-bold mb-2">Server Efficiency Dashboard</h2>
      <p className="text-sm text-gray-400 mb-4">Compare servers based on revenue per kilowatt and ROI</p>
      
      <div className="space-y-4">
        {serverMetrics.map(server => (
          <div key={server.id} className="bg-gray-900 rounded-lg p-3">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="font-semibold">{server.name}</h3>
                <p className="text-xs text-gray-400">{server.description}</p>
              </div>
              <span className="text-sm font-medium bg-blue-900 text-blue-200 px-2 py-1 rounded">
                {formatCurrency(server.cost)}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <div className="text-xs text-gray-400 mb-1">Revenue per kW</div>
                <div className="font-medium text-green-400">{formatCurrency(server.revenuePerKw, 2)}/kW</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">ROI (months)</div>
                <div className="font-medium">{server.roi.toFixed(1)} months</div>
              </div>
            </div>
            
            {/* Efficiency score bar */}
            <div className="mt-2">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Efficiency Score</span>
                <span>{server.efficiencyScore.toFixed(1)}</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div 
                  className={`${getBarColor(server.efficiencyScore)} h-2 rounded-full`}
                  style={{ width: `${Math.min(server.efficiencyScore * 2, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServerEfficiencyDashboard;
